/**
 * B2 (E-1.B.1-02): GHL API Connect step
 *
 * Enter API key → validate against GHL API → show account info.
 * API key encrypted at rest (DEK), never logged, never persisted client-side.
 */
import { useState } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import type { WizardState } from "../wizard-shell";

interface ConnectStepProps {
  state: WizardState;
  goNext: () => void;
  updateState: (patch: Partial<WizardState>) => void;
}

export function ConnectStep({ state, goNext, updateState }: ConnectStepProps) {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    if (!apiKey.trim()) {
      setError(t("migration.connect.errorEmpty"));
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await apiFetch<{
        valid: boolean;
        account: { name: string; locationCount: number; contactCount: number };
        jobId: string;
      }>("/platform/migration/validate-ghl", {
        method: "POST",
        body: JSON.stringify({ apiKey: apiKey.trim() }),
      });

      if (!result.valid) {
        setError(t("migration.connect.errorInvalid"));
        return;
      }

      updateState({
        ghlApiKey: apiKey.trim(),
        ghlAccount: result.account,
        jobId: result.jobId,
      });
      goNext();
    } catch {
      setError(t("migration.connect.errorNetwork"));
    } finally {
      setIsValidating(false);
    }
  };

  // If already connected, show account info
  if (state.ghlAccount) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4">
          <p className="text-sm font-medium text-[var(--color-success)]">
            {t("migration.connect.connected")}
          </p>
          <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">{t("migration.connect.accountName")}</p>
              <p className="font-medium text-foreground">{state.ghlAccount.name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("migration.connect.locations")}</p>
              <p className="font-medium text-foreground">{state.ghlAccount.locationCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">{t("migration.connect.contacts")}</p>
              <p className="font-medium text-foreground">{state.ghlAccount.contactCount.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => updateState({ ghlAccount: null, ghlApiKey: null })}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          {t("migration.connect.reconnect")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {t("migration.connect.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("migration.connect.description")}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground">
          {t("migration.connect.apiKeyLabel")}
        </label>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="ghl_xxxxxxxxxxxxxxxx"
          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          autoComplete="off"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {t("migration.connect.apiKeyHelp")}
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <button
        onClick={handleValidate}
        disabled={isValidating || !apiKey.trim()}
        className="rounded-md bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        {isValidating ? t("common.loading") : t("migration.connect.validate")}
      </button>
    </div>
  );
}
